// src/pages/admin/AboutContentManager.jsx
import React, { useState, useEffect } from 'react';
import {
  Container, Row, Col, Card, Form, Button, Table,
  Modal, Badge, Alert, Spinner, Image
} from 'react-bootstrap';
import axios from 'axios';
import AdminNavbar from './components/AdminNavbar';
import { BASE_URL, UPLOAD_BASE_URL } from '../../services/api';

const AboutContentManager = () => {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [user, setUser] = useState(null);
  
  // Form states
  const [showModal, setShowModal] = useState(false);
  const [editingSection, setEditingSection] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  
  const [formData, setFormData] = useState({
    section_title: '',
    content: '',
    image_url: '',
    button_text: 'Learn More',
    button_link: '/about',
    display_order: 0,
    is_active: true
  });

  const API_BASE_URL = BASE_URL;
  const imageUrl = `${UPLOAD_BASE_URL}${formData.image_url}`;
  
  useEffect(() => {
    // Fetch user data from localStorage or API
    const userData = JSON.parse(localStorage.getItem('user') || 'null');
    setUser(userData);
    
    fetchSections();
  }, []);

  const resetForm = () => {
    setFormData({
      section_title: '',
      content: '',
      image_url: '',
      button_text: 'Learn More',
      button_link: '/about',
      display_order: 0,
      is_active: true
    });
    setImagePreview(null);
  };

  const handleLogout = () => {
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    window.location.href = '/admin/login';
  };

  const fetchSections = async () => {
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('token');
      
      const response = await axios.get(`${API_BASE_URL}/about_content.php?admin=true`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.data.success) {
        const processedSections = response.data.data.map(section => ({
          ...section,
          id: parseInt(section.id),
          display_order: parseInt(section.display_order),
          is_active: section.is_active === "1" || section.is_active === 1,
          created_at: section.created_at
        })).sort((a, b) => {
          if (a.display_order === b.display_order) {
            return new Date(b.created_at) - new Date(a.created_at);
          }
          return a.display_order - b.display_order;
        });
        
        setSections(processedSections);
      } else {
        setError('Failed to load about sections');
      }
    } catch (err) {
      setError('Error connecting to server');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleTextareaChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
      
      // Set file for upload
      setFormData(prev => ({
        ...prev,
        image_file: file
      }));
    }
  };

  const removeImage = () => {
    setImagePreview(null);
    setFormData(prev => ({
      ...prev,
      image_url: '',
      image_file: null
    }));
  };

  const uploadImage = async (file) => {
    try {
      setUploadingImage(true);
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('image', file);
      
      const response = await axios.post(`${API_BASE_URL}/upload.php`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (response.data.success) {
        return response.data.image_url;
      }
      return null;
    } catch (err) {
      console.error('Image upload error:', err);
      return null;
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      let dataToSend = { ...formData };
      
      // Remove image_file from dataToSend if it exists
      if (dataToSend.image_file) {
        delete dataToSend.image_file;
      }
      
      // If there's a new image to upload
      if (formData.image_file) {
        const uploadedUrl = await uploadImage(formData.image_file);
        if (uploadedUrl) {
          dataToSend.image_url = uploadedUrl;
        } else {
          setError('Failed to upload image');
          return;
        }
      }
      
      const token = localStorage.getItem('token');
      let response;
      
      if (editingSection) {
        response = await axios.put(
          `${API_BASE_URL}/about_content.php?id=${editingSection.id}`,
          dataToSend,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
      } else {
        response = await axios.post(`${API_BASE_URL}/about_content.php`, dataToSend, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
      }
      
      if (response.data.success) {
        setSuccess(editingSection ? 'Section updated!' : 'Section added!');
        setShowModal(false);
        resetForm();
        setEditingSection(null);
        fetchSections();
        
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError('Operation failed: ' + (response.data.message || ''));
      }
    } catch (err) {
      setError('Error saving section: ' + (err.response?.data?.message || err.message));
      console.error(err);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this section?')) {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.delete(`${API_BASE_URL}/about_content.php?id=${id}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (response.data.success) {
          setSuccess('Section deleted!');
          fetchSections();
          setTimeout(() => setSuccess(''), 3000);
        } else {
          setError('Failed to delete section: ' + (response.data.message || ''));
        }
      } catch (err) {
        setError('Error deleting section');
        console.error(err);
      }
    }
  };

  const handleEdit = (section) => {
    setEditingSection(section);
    setFormData({
      section_title: section.section_title,
      content: section.content || '',
      image_url: section.image_url || '',
      button_text: section.button_text || 'Learn More',
      button_link: section.button_link || '/about',
      display_order: parseInt(section.display_order),
      is_active: section.is_active === true || section.is_active === "1" || section.is_active === 1
    });
    
    // Set image preview if image_url exists
    if (section.image_url) {
      setImagePreview(section.image_url);
    }
    
    setShowModal(true);
  };

  const handleReorder = async (direction, section) => {
    try {
      const currentSections = [...sections];
      const currentIndex = currentSections.findIndex(item => item.id === section.id);
      
      const token = localStorage.getItem('token');
      
      if (direction === 'up' && currentIndex > 0) {
        const prevSection = currentSections[currentIndex - 1];
        
        await axios.put(`${API_BASE_URL}/about_content.php?id=${section.id}`, {
          ...section,
          display_order: prevSection.display_order,
          is_active: (section.is_active === true || section.is_active === "1" || section.is_active === 1) ? 1 : 0
        }, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        await axios.put(`${API_BASE_URL}/about_content.php?id=${prevSection.id}`, {
          ...prevSection,
          display_order: section.display_order,
          is_active: (prevSection.is_active === true || prevSection.is_active === "1" || prevSection.is_active === 1) ? 1 : 0
        }, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        setSuccess('Order updated!');
        setTimeout(() => setSuccess(''), 3000);
        
      } else if (direction === 'down' && currentIndex < currentSections.length - 1) {
        const nextSection = currentSections[currentIndex + 1];
        
        await axios.put(`${API_BASE_URL}/about_content.php?id=${section.id}`, {
          ...section,
          display_order: nextSection.display_order,
          is_active: (section.is_active === true || section.is_active === "1" || section.is_active === 1) ? 1 : 0
        }, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        await axios.put(`${API_BASE_URL}/about_content.php?id=${nextSection.id}`, {
          ...nextSection,
          display_order: section.display_order,
          is_active: (nextSection.is_active === true || nextSection.is_active === "1" || nextSection.is_active === 1) ? 1 : 0
        }, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        setSuccess('Order updated!');
        setTimeout(() => setSuccess(''), 3000);
      }
      
      fetchSections();
      
    } catch (err) {
      console.error('Error reordering:', err);
      setError('Failed to reorder: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleToggleActive = async (section) => {
    try {
      const currentActive = section.is_active === true || section.is_active === "1" || section.is_active === 1;
      const newActiveStatus = !currentActive;
      
      const updatedData = {
        section_title: section.section_title,
        content: section.content || '',
        image_url: section.image_url || '',
        button_text: section.button_text || 'Learn More',
        button_link: section.button_link || '/about',
        display_order: parseInt(section.display_order),
        is_active: newActiveStatus ? 1 : 0
      };
      
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${API_BASE_URL}/about_content.php?id=${section.id}`,
        updatedData,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      if (response.data.success) {
        fetchSections();
      } else {
        setError('Failed to update status: ' + (response.data.message || ''));
      }
    } catch (err) {
      console.error('Error toggling active status:', err);
      setError('Failed to update status');
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingSection(null);
    resetForm();
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const truncateText = (text, maxLength = 100) => {
    if (!text) return 'No content';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  if (loading) {
    return (
      <>
        <AdminNavbar onLogout={handleLogout} user={user} />
        <Container className="mt-5 text-center">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3">Loading about sections...</p>
        </Container>
      </>
    );
  }

  return (
    <>
      <AdminNavbar onLogout={handleLogout} user={user} />
      <Container fluid className="py-4">
        <Row className="mb-4">
          <Col>
            <h2>About Content Manager</h2>
            <p className="text-muted">Manage your website about page sections</p>
          </Col>
          <Col className="text-end">
            <Button variant="primary" onClick={() => {
              const nextOrder = sections.length > 0 
                ? Math.max(...sections.map(section => parseInt(section.display_order))) + 1 
                : 1;
              setFormData(prev => ({ 
                ...prev, 
                section_title: '',
                content: '',
                image_url: '',
                button_text: 'Learn More',
                button_link: '/about',
                display_order: nextOrder,
                is_active: true
              }));
              setShowModal(true);
            }}>
              <i className="fas fa-plus me-2"></i>Add Section
            </Button>
          </Col>
        </Row>

        {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}
        {success && <Alert variant="success" onClose={() => setSuccess('')} dismissible>{success}</Alert>}

        <Card>
          <Card.Body>
            <Table responsive hover>
              <thead>
                <tr>
                  <th width="50">Order</th>
                  <th>Section Title</th>
                  <th>Content Preview</th>
                  <th>Image</th>
                  <th>Button</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th width="150">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sections.map((section, index) => {
                  const serialNumber = index + 1;
                  
                  return (
                    <tr key={section.id}>
                      <td>
                        <div className="d-flex align-items-center">
                          <Button
                            size="sm"
                            variant="outline-secondary"
                            onClick={() => handleReorder('up', section)}
                            disabled={parseInt(section.display_order) <= 1}
                          >
                            <i className="fas fa-arrow-up"></i>
                          </Button>
                          <span className="mx-2">{serialNumber}</span>
                          <Button
                            size="sm"
                            variant="outline-secondary"
                            onClick={() => handleReorder('down', section)}
                            disabled={parseInt(section.display_order) >= sections.length}
                          >
                            <i className="fas fa-arrow-down"></i>
                          </Button>
                        </div>
                      </td>
                      <td>
                        <strong>{section.section_title}</strong>
                      </td>
                      <td>
                        <small className="text-muted">
                          {truncateText(section.content, 80)}
                        </small>
                      </td>
                      <td>
                        {section.image_url ? (
                          <Image 
                            src={`${UPLOAD_BASE_URL}${section.image_url}`} 
                            alt={section.section_title}
                            thumbnail 
                            style={{ width: '60px', height: '40px', objectFit: 'cover' }}
                          />
                        ) : (
                          <span className="text-muted">No image</span>
                        )}
                      </td>
                      <td>
                        {section.button_text && (
                          <Badge bg="info" className="me-2">
                            {section.button_text}
                          </Badge>
                        )}
                        <small className="text-muted d-block">{section.button_link}</small>
                      </td>
                      <td>
                        <Form.Check
                          type="switch"
                          checked={section.is_active === true || section.is_active === "1" || section.is_active === 1}
                          onChange={() => handleToggleActive(section)}
                          label={(section.is_active === true || section.is_active === "1" || section.is_active === 1) ? 'Active' : 'Inactive'}
                          key={`active-${section.id}-${section.is_active}`}
                        />
                      </td>
                      <td>
                        <small className="text-muted">
                          {formatDate(section.created_at)}
                        </small>
                      </td>
                      <td>
                        <Button
                          size="sm"
                          variant="outline-primary"
                          className="me-2"
                          onClick={() => handleEdit(section)}
                        >
                          <i className="fas fa-edit"></i>
                        </Button>
                        
                        <Button
                          size="sm"
                          variant="outline-danger"
                          onClick={() => handleDelete(section.id)}
                        >
                          <i className="fas fa-trash"></i>
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
            
            {sections.length === 0 && (
              <div className="text-center py-5 text-muted">
                <i className="fas fa-info-circle fa-3x mb-3"></i>
                <p>No about sections found. Add your first section!</p>
              </div>
            )}
          </Card.Body>
        </Card>

        {/* Add/Edit Section Modal */}
        <Modal show={showModal} onHide={handleCloseModal} size="xl">
          <Modal.Header closeButton>
            <Modal.Title>
              {editingSection ? 'Edit About Section' : 'Add About Section'}
            </Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleSubmit}>
            <Modal.Body>
              <Row>
                <Col md={8}>
                  <Form.Group className="mb-3">
                    <Form.Label>Section Title *</Form.Label>
                    <Form.Control
                      type="text"
                      name="section_title"
                      value={formData.section_title}
                      onChange={handleInputChange}
                      required
                      placeholder="e.g., Our Mission, Our Story, Our Team"
                    />
                    <Form.Text className="text-muted">
                      Title for this about section
                    </Form.Text>
                  </Form.Group>
                </Col>
                
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Display Order</Form.Label>
                    <Form.Control
                      type="number"
                      name="display_order"
                      value={formData.display_order}
                      onChange={handleInputChange}
                      min="1"
                    />
                    <Form.Text className="text-muted">
                      Lower numbers appear first
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
              
              <Form.Group className="mb-3">
                <Form.Label>Content *</Form.Label>
                <Form.Control
                  as="textarea"
                  name="content"
                  value={formData.content}
                  onChange={handleTextareaChange}
                  rows={6}
                  required
                  placeholder="Enter detailed content for this section..."
                />
                <Form.Text className="text-muted">
                  Supports HTML formatting
                </Form.Text>
              </Form.Group>
              
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Image Upload</Form.Label>
                    <Form.Control
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      disabled={uploadingImage}
                    />
                    <Form.Text className="text-muted">
                      Upload an image for this section (optional)
                    </Form.Text>
                  </Form.Group>
                </Col>
                
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Status</Form.Label>
                    <div>
                      <Form.Check
                        type="switch"
                        name="is_active"
                        label="Active"
                        checked={formData.is_active}
                        onChange={handleInputChange}
                      />
                    </div>
                    <Form.Text className="text-muted">
                      Inactive sections won't be shown on the website
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
              
              {/* Image Preview */}
              {(imagePreview || formData.image_url) && (
                <div className="mb-3">
                  <Form.Label>Image Preview</Form.Label>
                  <div className="d-flex align-items-center">
                    <Image 
                      src={`${UPLOAD_BASE_URL}${formData.image_url}`}
                      alt="Preview" 
                      thumbnail 
                      style={{ maxWidth: '200px', maxHeight: '150px' }}
                      className="me-3"
                    />
                    
                    
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={removeImage}
                    >
                      <i className="fas fa-trash me-1"></i> Remove Image
                    </Button>
                  </div>
                </div>
              )}
              
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Button Text</Form.Label>
                    <Form.Control
                      type="text"
                      name="button_text"
                      value={formData.button_text}
                      onChange={handleInputChange}
                      placeholder="e.g., Learn More, Read More, Get Started"
                    />
                    <Form.Text className="text-muted">
                      Leave empty to hide button
                    </Form.Text>
                  </Form.Group>
                </Col>
                
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Button Link</Form.Label>
                    <Form.Control
                      type="text"
                      name="button_link"
                      value={formData.button_link}
                      onChange={handleInputChange}
                      placeholder="/about, /contact, https://..."
                    />
                    <Form.Text className="text-muted">
                      Internal path or external URL
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
              
              {/* Sample HTML Content */}
              {/* <div className="alert alert-info">
                <small>
                  <i className="fas fa-lightbulb me-2"></i>
                  <strong>HTML Tips:</strong> You can use HTML tags in content: 
                  </small>
              </div> */}
              
              {editingSection && (
                <div className="alert alert-secondary">
                  <small>
                    <i className="fas fa-info-circle me-2"></i>
                    Created: {formatDate(editingSection.created_at)}
                  </small>
                </div>
              )}
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={handleCloseModal}>
                Cancel
              </Button>
              <Button variant="primary" type="submit" disabled={uploadingImage}>
                {uploadingImage && <Spinner animation="border" size="sm" className="me-2" />}
                {editingSection ? 'Update' : 'Add'} Section
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>
      </Container>
    </>
  );
};

export default AboutContentManager;